import { RequestData } from '@ant-design/pro-table';
import { SortOrder } from 'antd/es/table/interface';
import { request } from '@/utils/request';
import { silentRequest } from '@/utils/http';

export interface SupportCenterListParams {
  teamId?: string;
  statuses?: string[];
  portalIssueReferenceId?: string;
  linearIdentifier?: string;
  labels?: string[];
  title?: string;
  searchKeyword?: string;
  reportStartDate?: string;
  reportEndDate?: string;
  occurredStartDate?: string;
  occurredEndDate?: string;
  resolvedStartDate?: string;
  resolvedEndDate?: string;
  majorOnly?: boolean;
  reporter?: string;
  reporterOperatorId?: string;
  assignee?: string;
  assignedTeam?: string;
  ticketType?: string;
  clientUserId?: string;
  tagIds?: string[];
  current: number;
  pageSize: number;
  sorter?: Record<string, SortOrder>;
  vendorId?: number;
  includeClientOutstandingCount?: boolean;
}

export enum SupportCenterEventTypes {
  IssueStatusChanged = 'support_center.event.issue_status_changed',
  IssueSeverityChanged = 'support_center.event.issue_severity_changed',
  IssueCommentAdded = 'support_center.event.issue_comment_added',
  IssueAssigneeChanged = 'support_center.event.issue_assignee_changed',
  IssueCreated = 'support_center.event.issue_created',
  IssueTitleChanged = 'support_center.event.issue_title_changed'
}

export enum IssueStatus {
  'Submitted' = 'Submitted',
  'Resolved' = 'Resolved',
  'In Review' = 'In Review',
  'In Progress' = 'In Progress',
  'Awaiting Your Response' = 'Awaiting Your Response',
  'Checking with Client/COB' = 'Checking with Client/COB',
  'Scheduled' = 'Scheduled',
  'Closed' = 'Closed',
  'Blocked' = 'Blocked'
}

/**
 * Fallback copy of backend OUTSTANDING_STATUSES (linear-api.service.ts line ~50).
 * Prefer using outstandingStatuses from the getClientOutstandingIssueCount API
 * response when available. SupportCenterList uses this for URL param resolution
 * where the API response is not accessible.
 *
 * WARNING: If you change this list, you MUST also update the backend constant
 * in projects/utx/src/support-center/linear-api.service.ts
 */
export const OUTSTANDING_STATUSES: string[] = ['Submitted', 'Scheduled', 'In Progress', 'In Review', 'Awaiting Your Response', 'Checking with Client/COB', 'Blocked'];

/** Matches backend LinearApiService.COUNT_BATCH_SIZE / countIssues cap. */
export const OUTSTANDING_COUNT_CAP = 250;

export const severityOptions = [
  { label: 'Urgent', value: 'Urgent' },
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' }
];

export const priorityOptions = [
  { label: 'Critical', value: 'Critical' },
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' }
];

// "operator" maps to NULL or 'issue' in DB (legacy issues before client_issue was introduced).
// Backend handles via `(ticket_type IS NULL OR ticket_type = 'issue')`.
// Other types (feedback, feature_request, client_issue) are matched exactly.
export const ticketTypeConfig: Record<string, { label: string; color?: string }> = {
  client_issue: { label: 'Client Issue', color: 'orange' },
  operator: { label: 'Operator Issue', color: 'red' },
  issue: { label: 'Operator Issue', color: 'red' },
  feedback: { label: 'Feedback', color: 'blue' },
  feature_request: { label: 'Feature Request', color: 'purple' },
};

export const ticketTypeOptions = [
  { label: 'Client Issue', value: 'client_issue' },
  { label: 'Operator Issue', value: 'operator' },
  { label: 'Feedback', value: 'feedback' },
  { label: 'Feature Request', value: 'feature_request' },
];

export function getTicketTypeLabel(ticketType?: string): string {
  if (!ticketType) return 'Ticket';
  return ticketTypeConfig[ticketType]?.label ?? ticketType;
}

export function getTicketTypeColor(ticketType?: string): string | undefined {
  if (!ticketType) return undefined;
  return ticketTypeConfig[ticketType]?.color;
}

/** Extract display name from UserSelect option label (format: "Name — email · account · vendor"). */
export function extractClientNameFromOption(option: { label?: unknown; children?: unknown }): string {
  let text = '';
  if (typeof option.label === 'string') {
    text = option.label;
  } else if (typeof option.children === 'string') {
    text = option.children;
  } else if (Array.isArray(option.children)) {
    text = option.children.map(String).join('');
  }
  const raw = text.split(' — ')[0]?.split(' - ')[0]?.trim() || '';
  return raw === '-' ? '' : raw;
}

export const issueTypeOptions = [
  { label: 'Data Inaccuracy', value: 'Data Inaccuracy' },
  { label: 'UI Bug', value: 'UI Bug' },
  { label: 'Missing Information', value: 'Missing Information' },
  { label: 'AI Extraction Error', value: 'AI Extraction Error' },
  { label: 'Performance Issue', value: 'Performance Issue' },
  { label: 'Feature Request', value: 'Feature Request' },
  { label: 'System Error', value: 'System Error' },
  { label: 'Other', value: 'Other' }
];

export const bugCategoryOptions = [
  { label: 'UI Bug', value: 'UI Bug' },
  { label: 'Data Issue', value: 'Data Issue' },
  { label: 'Performance', value: 'Performance' },
  { label: 'AI Error', value: 'AI Error' },
  { label: 'System Error', value: 'System Error' }
];

export const feedbackTypeOptions = [
  { label: 'Positive Feedback', value: 'Positive' },
  { label: 'Improvement Suggestion', value: 'Improvement' },
  { label: 'Concern', value: 'Concern' }
];

export interface TicketTag {
  id: string;
  vendorId: number;
  name: string;
  color: string;
  isActive: boolean;
  createdByOperatorId: string;
  updatedByOperatorId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupportCenterListResponse {
  issues: Array<SupportCenterListItem>;
  total?: number;
  clientOutstandingCount?: number;
  outstandingStatuses?: string[];
  error?: string;
}

export interface SupportCenterOutstandingStatisticsResponse {
  totalOutstandingCount: number;
  totalInProgressCount: number;
  totalHighPriorityCount: number;
}

export interface SupportCenterListItem {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  dueDate: string;
  portalIssueReferenceId: string;
  reporterOperatorId?: string;
  reporterOperatorName?: string;
  linearIdentifier: string;
  url: string;
  assigneeId: string;
  assigneeName: string;
  assignedTeamId: string;
  assignedTeamName: string;
  operatorAssignedSeverity?: string;
  operatorAssignedIssueType?: string;
  ticketType?: string;
  /** Department tags for team board visibility */
  departmentTags?: string[];
  primaryClientName?: string;
  affectedClientCount?: number;
  overdueActionCount?: number;
  categoryDisplay?: string;
  lastActionDescription?: string;
  tags?: TicketTag[];
  occurredOn?: string | null;
  resolvedAt?: string | null;
  linearSyncStatus?: 'synced' | 'pending' | 'failed';
  linearSyncError?: string;
  vendorId?: number;
}

export interface IssueCommentNode {
  id: string;
  body: string;
  author: string;
  createdAt: Date;
  updatedAt?: Date;
  replies: IssueCommentNode[];
}

export interface ClientEntry {
  user_id: string;
  display_name: string;
  account_ref?: string;
  card_ref?: string;
  card_embossed_name?: string;
  card_product_name?: string;
  card_last4?: string;
  transaction_ref?: string;
  transaction_url?: string;
  is_primary: boolean;
  added_by_operator_id: string;
  added_at: string;
}

export interface ActionItem {
  id: string;
  description: string;
  remarks?: string;
  type: string;
  due_date?: string;
  attachments?: SupportCenterAttachment[];
  status: string;
  completion_notes?: string;
  completed_at?: string;
  completed_by_name?: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
}

export const actionStatusOptions = [
  { label: 'Submitted', value: 'Submitted' },
  { label: 'In Review', value: 'In Review' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Awaiting Your Response', value: 'Awaiting Your Response' },
  { label: 'Checking with Client/COB', value: 'Checking with Client/COB' },
  { label: 'Scheduled', value: 'Scheduled' },
  { label: 'Resolved', value: 'Resolved' },
  { label: 'Closed', value: 'Closed' },
  { label: 'Blocked', value: 'Blocked' },
  { label: 'Cancelled', value: 'Cancelled' }
];

export interface SupportCenterAttachment {
  name: string;
  url: string;
  linearUrl?: string;
  utxKey?: string;
  utxUrl?: string;
}

export interface CategoryTreeItem {
  id: string;
  name: string;
  displayOrder: number;
  isSystem: boolean;
  isActive: boolean;
  children: CategoryTreeItem[];
}

export interface SupportCenterDetail {
  id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  portalIssueReferenceId: string;
  reporterOperatorId?: string;
  reporterOperatorName?: string;
  comments: IssueCommentNode[];
  followers: {
    id: string;
    name: string;
    email: string;
  }[];
  linearIdentifier: string;
  url: string;
  assigneeId: string;
  assigneeName: string;
  assignedTeamId: string;
  assignedTeamName: string;
  operatorAssignedSeverity?: string;
  operatorAssignedIssueType?: string;
  dueDate: string;
  vendorId: number;
  /** Department tags for team board visibility */
  departmentTags?: string[];
  ticketType?: string;
  categoryId?: string;
  categoryName?: string;
  subCategoryId?: string;
  subCategoryName?: string;
  otherCategoryText?: string;
  impactScope?: string;
  clients?: ClientEntry[];
  actions?: ActionItem[];
  tags?: TicketTag[];
  occurredOn?: string | null;
  resolvedAt?: string | null;
  linearSyncStatus?: 'synced' | 'pending' | 'failed';
  linearSyncError?: string;
}

export interface EligibleFollowerOperator {
  id: string;
  name: string;
  email: string;
  vendorIds: number[] | null;
  vendorsName: string[];
  departmentTags: string[];
}

export interface IssueCommentRequest {
  issueId: string;
  body: string;
  parentId?: string;
  attachments?: SupportCenterAttachment[];
  commentId?: string;
}

export async function getSupportCenterList(params: SupportCenterListParams): Promise<RequestData<SupportCenterListResponse>> {
  return await request<RequestData<SupportCenterListResponse>>('/issues', {
    params
  });
}

/**
 * Lists client issues for one client. The API requires clientUserId and
 * authorizes this narrower view with VIEW:SUPPORT_CENTER_CLIENT_ISSUE, so it
 * cannot be used to browse the global Support Center queue.
 */
export async function getClientSupportCenterList(params: SupportCenterListParams): Promise<RequestData<SupportCenterListResponse>> {
  return await request<RequestData<SupportCenterListResponse>>('/issues/client-issues', {
    params
  });
}

export async function getClientOutstandingIssueCount(
  userId: string,
  vendorId: number
): Promise<{ count: number; outstandingStatuses: string[] }> {
  return await silentRequest<{ count: number; outstandingStatuses: string[] }>(
    `/issues/client-outstanding-count/${userId}`,
    { params: { vendorId } }
  );
}

export async function getMySupportCenterList(params: SupportCenterListParams): Promise<RequestData<SupportCenterListResponse>> {
  return await request<RequestData<SupportCenterListResponse>>('/issues/my-issues', {
    params
  });
}

export async function getMySupportCenterListSilently(params: SupportCenterListParams): Promise<RequestData<SupportCenterListResponse>> {
  return await silentRequest<RequestData<SupportCenterListResponse>>('/issues/my-issues', {
    params
  });
}

export async function getSupportCenterOutstandingStatistics(vendorId?: number): Promise<SupportCenterOutstandingStatisticsResponse> {
  return await request<SupportCenterOutstandingStatisticsResponse>('/issues/outstanding-statistics', {
    method: 'GET',
    params: {
      vendorId
    }
  });
}

export async function getSupportCenterOutstandingStatisticsSilently(vendorId?: number): Promise<SupportCenterOutstandingStatisticsResponse> {
  return await silentRequest<SupportCenterOutstandingStatisticsResponse>('/issues/outstanding-statistics', {
    method: 'GET',
    params: {
      vendorId
    }
  });
}

// Returns SupportCenterDetail directly (not { data }). All callers use useRequest() which wraps the result as { data }.
export async function querySupportCenterDetail(params: { id: string }): Promise<SupportCenterDetail> {
  if (!params.id) throw new Error('Issue ID is required');
  return await request<SupportCenterDetail>(`/issues/${params.id}`);
}

export async function queryClientSupportCenterDetail(params: { id: string; clientUserId: string }): Promise<SupportCenterDetail> {
  if (!params.id) throw new Error('Issue ID is required');
  if (!params.clientUserId) throw new Error('Client user ID is required');
  return await request<SupportCenterDetail>(`/issues/client-issues/${params.id}`, {
    params: { clientUserId: params.clientUserId }
  });
}

export async function queryEligibleSupportCenterFollowers(params: {
  vendorId: number;
  departmentOnly?: boolean;
}): Promise<{ operators: EligibleFollowerOperator[] }> {
  if (!params.vendorId) throw new Error('Vendor ID is required');
  return await request<{ operators: EligibleFollowerOperator[] }>('/issues/eligible-followers', {
    params: { vendorId: params.vendorId, departmentOnly: params.departmentOnly }
  });
}

export type IssueStatusHistoryField = 'status' | 'severity' | 'title' | 'action_status' | 'release';

export type IssueStatusHistorySource = 'operator' | 'linear';

export type IssueStatusHistoryEntry = {
  id: string;
  field: IssueStatusHistoryField;
  oldValue: string | null;
  newValue: string;
  source: IssueStatusHistorySource;
  actorName: string;
  createdAt: string;
};

export async function queryIssueStatusHistory(issueId: string): Promise<IssueStatusHistoryEntry[]> {
  if (!issueId) throw new Error('Issue ID is required');
  return await request<IssueStatusHistoryEntry[]>(`/issues/${issueId}/status-history`);
}

export async function updateIssueFollowers(params: {
  id: string;
  followers: string[];
  source?: 'duplicate_detection' | 'manual';
}) {
  if (!params.id) throw new Error('Issue ID is required');
  if (!Array.isArray(params.followers)) throw new Error('Followers must be an array');
  return await request<boolean>(`/issues/${params.id}/followers`, {
    method: 'PUT',
    data: params
  });
}

// --- Duplicate Detection (UTXMO-4409) ---

export interface SimilarIssue {
  linearTaskId: string;
  portalIssueReferenceId: string;
  linearIdentifier: string;
  title: string;
  status: string;
  severity?: string;
  similarityScore: number;
  matchReason: string;
  createdAt: string;
  reporterName?: string;
  isFollowing: boolean;
}

export type FindSimilarDegradedReason = 'timeout' | 'error';

/** Aligns with ops-api `FindSimilarResponse` — when degraded, `matches` is not proof of "no duplicates". */
export interface FindSimilarIssuesResponse {
  matches: SimilarIssue[];
  degradedReason?: FindSimilarDegradedReason;
  degradedMessage?: string;
}

export async function findSimilarIssues(params: {
  description: string;
  ticketType: string;
  vendorId: number;
  clientIds?: string[];
}): Promise<FindSimilarIssuesResponse> {
  return silentRequest<FindSimilarIssuesResponse>('/issues/find-similar', {
    method: 'POST',
    data: params
  });
}

export async function updateIssueStatusSeverity(params: { id: string; status?: string; severity?: string }) {
  if (!params.id) throw new Error('Issue ID is required');
  if (!params.status && !params.severity) {
    throw new Error('At least one field (status or severity) must be provided');
  }

  return await request<{ id: string; status: string; severity: string }>(`/issues/${params.id}/status-severity`, {
    method: 'PUT',
    data: {
      status: params.status,
      severity: params.severity
    }
  });
}

export async function updateIssueTitle(params: { id: string; title: string }) {
  if (!params.id) throw new Error('Issue ID is required');
  const title = params.title.trim();
  if (!title) throw new Error('Title is required');

  return await request<{ id: string; title: string }>(`/issues/${params.id}/title`, {
    method: 'PUT',
    data: { title }
  });
}

export async function updateIssueDepartmentTags(params: {
  id: string;
  departmentTags?: string[];
  occurredOn?: string | null;
}) {
  if (!params.id) throw new Error('Issue ID is required');
  if (params.departmentTags === undefined && params.occurredOn === undefined) {
    throw new Error('Provide departmentTags and/or occurredOn');
  }

  return await request<{ id: string; departmentTags: string[]; occurredOn?: string | null }>(`/issues/${params.id}/department-tags`, {
    method: 'PUT',
    data: {
      departmentTags: params.departmentTags,
      occurredOn: params.occurredOn
    }
  });
}

export async function addIssueComment(data: IssueCommentRequest) {
  if (!data.issueId) throw new Error('Issue ID is required');
  if (!data.body) throw new Error('Comment body is required');
  return await request<RequestData<SupportCenterDetail>>(`/issues/${data.issueId}/comments`, {
    method: 'POST',
    data
  });
}

export async function updateIssueComment(data: IssueCommentRequest) {
  if (!data.issueId) throw new Error('Issue ID is required');
  if (!data.commentId) throw new Error('Comment ID is required');
  if (!data.body) throw new Error('Comment body is required');
  return await request<RequestData<SupportCenterDetail>>(`/issues/${data.issueId}/comments/${data.commentId}`, {
    method: 'PUT',
    data
  });
}

export async function removeIssueComment(id: string, commentId: string) {
  if (!id) throw new Error('Issue ID is required');
  if (!commentId) throw new Error('Comment ID is required');
  return await request<RequestData<SupportCenterDetail>>(`/issues/${id}/comments/${commentId}`, {
    method: 'DELETE'
  });
}

export async function getIssueComments(issueId: string): Promise<IssueCommentNode[]> {
  if (!issueId) throw new Error('Issue ID is required');
  return await request<IssueCommentNode[]>(`/issues/${issueId}/comments`);
}

export const getIssueStatusColor = (status: string) => {
  switch (status) {
    case IssueStatus.Resolved:
      return '#28a745'; // green - successful resolution
    case IssueStatus.Submitted:
      return '#007bff'; // blue - info / just received
    case IssueStatus['In Review']:
      return '#17a2b8'; // teal - under evaluation
    case IssueStatus['In Progress']:
      return '#ffc107'; // yellow - active work
    case IssueStatus['Awaiting Your Response']:
      return '#fd7e14'; // orange - action required by user
    case IssueStatus['Checking with Client/COB']:
      return '#e8590c'; // deep orange - waiting on client/COB response
    case IssueStatus.Scheduled:
      return '#6f42c1'; // purple - planned for future action
    case IssueStatus.Closed:
      return '#6c757d'; // gray - finalized and inactive
    case IssueStatus.Blocked:
      return '#ff4d4f'; // red - blocked
    default:
      return '#343a40'; // dark gray - unknown status
  }
};


export const getSeverityColor = (severity?: string) => {
  switch (severity) {
    case 'Urgent':
      return 'red';
    case 'High':
      return '#ff7a45';
    case 'Medium':
      return 'blue';
    case 'Low':
      return 'gray';
    default:
      return 'gray';
  }
};

/**
 * Fetch team board issues - tickets with department tags matching the operator's departments.
 */
export async function getTeamSupportCenterList(params: SupportCenterListParams): Promise<RequestData<SupportCenterListResponse>> {
  return await request<RequestData<SupportCenterListResponse>>('/issues/team-issues', {
    params
  });
}

/**
 * Fetch the list of predefined department tags.
 */
export async function getDepartments(): Promise<{ departments: string[] }> {
  return await request<{ departments: string[] }>('/issues/departments');
}

/**
 * Predefined department tags for team board visibility.
 */
export const PREDEFINED_DEPARTMENT_TAGS = [
  'CS',
  'Sales',
  'Card',
  'Compliance',
  'Finance',
  'Tech',
  'Trust',
  'Onboarding',
  'Payment',
  'Banking'
] as const;

export type DepartmentTag = (typeof PREDEFINED_DEPARTMENT_TAGS)[number];

// --- Client Issue APIs (UTXMO-4224) ---

export const impactScopeOptions = [
  { label: 'Single Client', value: 'single_client' },
  { label: 'Multiple Clients', value: 'multiple_clients' },
  { label: 'All Clients', value: 'all_clients' }
];

export const actionTypeOptions = [
  { label: 'Call Client', value: 'call_client' },
  { label: 'Email Client', value: 'email_client' },
  { label: 'Internal Escalation', value: 'internal_escalation' },
  { label: 'Await 3rd Party', value: 'await_3rd_party' },
  { label: 'Send Document', value: 'send_document' },
  { label: 'Refund Processing', value: 'refund_processing' },
  { label: 'Manual Adjustment', value: 'manual_adjustment' },
  { label: 'Inform RM', value: 'inform_rm' },
  { label: 'Inform Client (TG Group)', value: 'inform_client_tg_group' },
  { label: 'Inform Client (Intercom)', value: 'inform_client_intercom' },
  { label: 'Report issue', value: 'report_issue' },
  { label: 'Other', value: 'other' }
];

export async function reportClientIssue(data: {
  title: string;
  description: string;
  severity: string;
  issueType?: string;
  impactScope: string;
  vendorId: number;
  categoryId: string;
  subCategoryId?: string;
  otherCategoryText?: string;
  clients: Array<{
    user_id: string;
    display_name: string;
    account_ref?: string;
    card_ref?: string;
    transaction_ref?: string;
    is_primary: boolean;
  }>;
  actions?: Array<{ description: string; remarks?: string; type: string; due_date?: string; attachments?: SupportCenterAttachment[] }>;
  attachments?: SupportCenterAttachment[];
  followers?: string[];
  departmentTags?: string[];
  /** Optional ticket tag UUIDs (vendor catalog), applied after issue creation */
  tagIds?: string[];
  duplicateCheckShown?: boolean;
  occurredOn?: string;
}): Promise<{
  portalIssueReferenceID: string;
  details?: {
    linearIssueId?: string;
    linearIdentifier?: string;
    linearSyncStatus?: 'synced' | 'pending' | 'failed';
    error?: string;
  };
}> {
  return request('/issues/report-client-issue', { method: 'POST', data });
}

export async function getCategories(vendorId: number, includeRetired = false): Promise<CategoryTreeItem[]> {
  return request<CategoryTreeItem[]>('/issues/categories', {
    params: { vendorId, includeRetired: includeRetired ? 'true' : undefined }
  });
}

export async function addClientsToIssue(issueId: string, clients: Array<{
  user_id: string;
  display_name: string;
  account_ref?: string;
  card_ref?: string;
  transaction_ref?: string;
  is_primary: boolean;
}>): Promise<{ clients: number }> {
  return request(`/issues/${issueId}/clients`, { method: 'PUT', data: { clients } });
}

export async function updateClientsOnIssue(issueId: string, clients: Array<{
  user_id: string;
  display_name: string;
  account_ref?: string;
  card_ref?: string;
  transaction_ref?: string;
  is_primary: boolean;
}>): Promise<{ clients: number; updatedClients: number }> {
  return request(`/issues/${issueId}/clients`, { method: 'PATCH', data: { clients } });
}

export async function removeClientsFromIssue(issueId: string, clientUserIds: string[]): Promise<{ clients: number; removedClients: number }> {
  return request(`/issues/${issueId}/clients`, { method: 'DELETE', params: { clientUserIds }, data: { clientUserIds } });
}

export async function addActionToIssue(issueId: string, action: {
  description: string;
  remarks?: string | unknown | null;
  type: string;
  due_date?: string;
  attachments?: SupportCenterAttachment[];
  status?: string;
}): Promise<{ id: string }> {
  return request(`/issues/${issueId}/actions`, { method: 'POST', data: action });
}

export async function updateAction(issueId: string, actionId: string, update: {
  status?: string;
  completion_notes?: string;
  due_date?: string;
  description?: string;
  remarks?: string;
  attachments?: SupportCenterAttachment[];
}): Promise<{ success: boolean }> {
  return request(`/issues/${issueId}/actions/${actionId}`, { method: 'PUT', data: update });
}

// --- Category Admin APIs ---

export interface CategoryAdminItem {
  id: string;
  name: string;
  displayOrder: number;
  isSystem: boolean;
  isActive: boolean;
  retiredAt?: string;
  parentId?: string;
  vendorId?: number;
  createdByOperatorId?: string;
  updatedByOperatorId?: string;
  createdAt?: string;
  updatedAt?: string;
  children?: CategoryAdminItem[];
}

export async function getCategoriesAdmin(vendorId: number, includeRetired = true): Promise<CategoryAdminItem[]> {
  return request<CategoryAdminItem[]>('/issues/categories', {
    params: { vendorId, includeRetired: includeRetired ? 'true' : 'false' }
  });
}

export async function createCategory(data: {
  vendorId: number;
  name: string;
  parentId?: string;
  displayOrder?: number;
}): Promise<{ id: string }> {
  return request('/issues/categories', { method: 'POST', data });
}

export async function updateCategory(categoryId: string, vendorId: number, data: {
  name?: string;
  displayOrder?: number;
}): Promise<{ success: boolean }> {
  return request(`/issues/categories/${categoryId}`, { method: 'PUT', params: { vendorId }, data });
}

export async function retireCategory(categoryId: string, vendorId: number): Promise<{ success: boolean }> {
  return request(`/issues/categories/${categoryId}/retire`, { method: 'PUT', params: { vendorId } });
}

export async function reactivateCategory(categoryId: string, vendorId: number): Promise<{ success: boolean }> {
  return request(`/issues/categories/${categoryId}/reactivate`, { method: 'PUT', params: { vendorId } });
}

export async function getTicketTags(vendorId: number, includeInactive = false): Promise<TicketTag[]> {
  return request<TicketTag[]>('/issues/tags', {
    params: { vendorId, includeInactive: includeInactive ? 'true' : undefined }
  });
}

export async function createTicketTag(data: { vendorId: number; name: string; color: string }): Promise<TicketTag> {
  return request<TicketTag>('/issues/tags', { method: 'POST', data });
}

export async function updateTicketTag(
  tagId: string,
  vendorId: number,
  data: { name?: string; color?: string }
): Promise<TicketTag> {
  return request<TicketTag>(`/issues/tags/${tagId}`, { method: 'PUT', params: { vendorId }, data });
}

export async function archiveTicketTag(tagId: string, vendorId: number): Promise<TicketTag> {
  return request<TicketTag>(`/issues/tags/${tagId}/archive`, { method: 'PUT', params: { vendorId } });
}

export async function restoreTicketTag(tagId: string, vendorId: number): Promise<TicketTag> {
  return request<TicketTag>(`/issues/tags/${tagId}/restore`, { method: 'PUT', params: { vendorId } });
}

export async function updateTicketTags(issueId: string, tagIds: string[]): Promise<{ id: string; tags: TicketTag[] }> {
  return request<{ id: string; tags: TicketTag[] }>(`/issues/${issueId}/tags`, {
    method: 'PUT',
    data: { tagIds }
  });
}

export interface OtherCategoryAnalyticsItem {
  text: string;
  count: number;
  latestDate: string;
}

export async function getOtherCategoryAnalytics(
  vendorId: number,
  startDate?: string,
  endDate?: string,
): Promise<{
  items: OtherCategoryAnalyticsItem[];
  totalIssues: number;
  totalCount: number;
  otherPercentage: number;
  thresholdExceeded: boolean;
}> {
  const now = new Date();
  let rangeStart: string;
  let rangeEnd: string;
  if (startDate === undefined && endDate === undefined) {
    const oneYearAgo = new Date(now);
    oneYearAgo.setFullYear(now.getFullYear() - 1);
    rangeStart = oneYearAgo.toISOString();
    rangeEnd = now.toISOString();
  } else {
    const defaultStart90 = new Date(now);
    defaultStart90.setDate(defaultStart90.getDate() - 90);
    rangeStart = startDate ?? defaultStart90.toISOString();
    rangeEnd = endDate ?? now.toISOString();
  }

  const resp = await request<{
    totalIssues: number;
    otherCount: number;
    otherPercentage: number;
    exceedsThreshold: boolean;
    topFreeTextValues: OtherCategoryAnalyticsItem[];
  }>('/issues/categories/analytics', {
    params: {
      vendorId,
      startDate: rangeStart,
      endDate: rangeEnd,
    },
  });

  return {
    items: resp.topFreeTextValues,
    totalIssues: resp.totalIssues,
    totalCount: resp.otherCount,
    otherPercentage: resp.otherPercentage,
    thresholdExceeded: resp.exceedsThreshold,
  };
}
